import type { ToolRunResult } from '@/services';

export type ToolRunTone = 'success' | 'partial' | 'neutral' | 'error';

interface ToolRunOutcome {
	tone: ToolRunTone;
	title: string;
	hint?: string;
}

const READ_SUCCESS: ToolRunOutcome = { tone: 'success', title: 'Connected and answering' };
const WRITE_SUCCESS: ToolRunOutcome = { tone: 'success', title: 'Done on your live integration' };
const FAILED: ToolRunOutcome = { tone: 'error', title: 'The run failed' };
const CHECK_TOOL_EVENTS = 'It may still go through: check Tool events before running it again.';
const STEPS_FELL_SHORT: ToolRunOutcome = {
	tone: 'partial',
	title: 'Finished, but some steps failed',
	hint: 'The AI still gets the steps that worked; a failed or skipped step has no data.'
};
const WAIT_A_MINUTE = 'Wait a minute, then run again.';
const ON_OUR_SIDE = 'This is on our side. Share the request id with support.';

/** What each tool server error code means for someone checking their integration. */
const BY_ERROR_CODE: Record<string, ToolRunOutcome> = {
	NOT_FOUND: { tone: 'neutral', title: 'Connected, but nothing matched these arguments' },
	TOOL_NOT_FOUND: { tone: 'error', title: 'Not connected for this brand' },
	PROVIDER_NOT_CONNECTED: { tone: 'error', title: 'Not connected for this brand' },
	AMBIGUOUS_PROVIDER: {
		tone: 'error',
		title: 'This brand has more than one connection for this integration',
		hint: 'Kim cannot tell which one to use. Remove the extra connection on the Integrations page.'
	},
	PROVIDER_ERROR: { tone: 'error', title: 'The integration answered with an error' },
	PROVIDER_RATE_LIMIT: { tone: 'error', title: 'The integration is limiting requests right now', hint: WAIT_A_MINUTE },
	INVALID_REQUEST: { tone: 'error', title: 'The tool refused these arguments' },
	ACCESS_DENIED: { tone: 'error', title: 'This tool cannot run as a test' },
	RATE_LIMITED: { tone: 'error', title: 'Too many requests, try again shortly' },
	SERVICE_BUSY: { tone: 'error', title: "Kim's tool service is busy", hint: WAIT_A_MINUTE },
	AUTH_ERROR: { tone: 'error', title: 'Kim could not reach the tool service', hint: ON_OUR_SIDE },
	TOOL_SERVER_ERROR: { tone: 'error', title: "Kim's tool service failed to answer", hint: ON_OUR_SIDE },
	TIMEOUT: { tone: 'error', title: 'The integration took too long to answer' }
};

const STEP_STATUSES = new Set(['success', 'simulated', 'error', 'skipped']);
const FELL_SHORT = new Set(['error', 'skipped']);

/** Each step's status when the data is a custom tool's answer, keyed by step; null for any other tool's data. */
const stepOutcomes = (data: unknown): string[] | null => {
	if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
	const outcomes = Object.values(data).map((step: unknown) => (step as { status?: unknown } | null)?.status);
	return outcomes.length > 0 && outcomes.every((status) => typeof status === 'string' && STEP_STATUSES.has(status))
		? (outcomes as string[])
		: null;
};

export const toolRunOutcome = (result: Pick<ToolRunResult, 'status' | 'code' | 'data'>, writes = false): ToolRunOutcome => {
	if (result.status === 'success') {
		// A custom tool succeeds when its writes do, even if a read step failed.
		if (stepOutcomes(result.data)?.some((status) => FELL_SHORT.has(status))) return STEPS_FELL_SHORT;
		return writes ? WRITE_SUCCESS : READ_SUCCESS;
	}
	const outcome = BY_ERROR_CODE[result.code ?? ''] ?? FAILED;
	return writes && result.code === 'TIMEOUT' ? { ...outcome, hint: CHECK_TOOL_EVENTS } : outcome;
};
